import { Suspense, lazy, useEffect, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { ErrorState } from "@/components/ui/States";
import { openPalletPlanPdf, usePalletPlan, type PalletLegend } from "@/lib/api/pallets";
// Uslublar PalletScene bilan BIRGA kelmaydi: u lazy yuklanadi va
// boshqaruv elementlari undan oldin chiziladi.
import "@/modules/pallets/pallets.css";
import "./orders.css";

// three.js (~600 KB) faqat oyna ochilganda yuklanadi: buyurtmalar
// ro'yxatini ochgan har bir xodim uni ko'tarib yurmasligi kerak.
const PalletScene = lazy(() =>
  import("@/modules/pallets/PalletScene").then((module) => ({ default: module.PalletScene })),
);

/**
 * Terishga chiqarish oynasidagi pallet rejasi.
 *
 * Nega aynan shu yerda: reja TERISHGA CHIQARISHDAN OLDIN ma'noli —
 * dispetcher qancha pallet chiqishini va reja ishonchli ekanini
 * ko'rib qaror qabul qiladi. Ilgari reja alohida «Pallet»
 * bo'limida edi va u yerga faqat chiqargandan KEYIN borish mumkin
 * edi, ya'ni reja qarorga ta'sir qila olmasdi.
 *
 * Shu sababli o'sha bo'limning SOZLAMALARI ham bu yerga ko'chdi:
 * stellaj balandligi rejani o'zgartiradi (demak qarorga ta'sir
 * qiladi), qatlam filtri esa pastdagi qatorni ko'rish uchun kerak.
 * Ular «Pallet» bo'limida qolganda dispetcher qaror oynasidan
 * chiqib, boshqa ekranda o'sha buyurtmani qaytadan izlashi kerak
 * bo'lardi.
 */

const FIELD_NAMES: Record<string, string> = {
  length_cm: "uzunlik",
  width_cm: "en",
  height_cm: "balandlik",
  weight_kg: "vazn",
};

const UNPLACED_REASONS: Record<string, string> = {
  footprint_exceeds_pallet: "pallet yuzasiga sig'maydi",
  height_exceeds_limit: "balandlik chegarasidan baland",
  weight_exceeds_limit: "vazn chegarasidan og'ir",
};

const cm = (mm: number) => (mm / 10).toLocaleString("uz", { maximumFractionDigits: 0 });
const kg = (g: number) => (g / 1000).toLocaleString("uz", { maximumFractionDigits: 0 });

function flags(item: PalletLegend): string {
  return [item.fragile ? "mo'rt" : "", item.no_stack ? "ustiga qo'yilmaydi" : ""]
    .filter(Boolean)
    .join(", ");
}

export function ReleasePallets({
  orderId,
  orderNumber,
}: {
  orderId: string;
  orderNumber: string;
}) {
  const [heightInput, setHeightInput] = useState("");
  const [maxHeight, setMaxHeight] = useState("");
  const [index, setIndex] = useState(0);
  const [visibleLayers, setVisibleLayers] = useState(99);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const plan = usePalletPlan(orderId, maxHeight);

  // Chegara o'zgarsa reja qayta hisoblanadi va eski pallet raqami
  // mavjud bo'lmay qolishi mumkin.
  useEffect(() => {
    setIndex(0);
    setVisibleLayers(99);
  }, [orderId, maxHeight]);

  if (plan.isLoading) {
    return <div className="skeleton" style={{ height: 260 }} />;
  }
  if (plan.error) {
    return <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />;
  }

  const data = plan.data?.plan;
  if (!data || data.pallets.length === 0) {
    return (
      <p className="field-hint">
        Pallet rejasi tuzilmadi — tovarlarning VGH si yetarli emas.
      </p>
    );
  }

  const pallet = data.pallets[Math.min(index, data.pallets.length - 1)]!;
  const layers = Math.min(visibleLayers, pallet.layers.length);
  const counts = new Map(pallet.skus.map((sku) => [sku.key, sku.boxes]));
  const legend = data.legend.filter((item) => counts.has(item.key));

  return (
    <div className="release-plan">
      <div className="release-plan-head">
        <b>{data.pallets.length} pallet</b>
        <span className="field-hint" style={{ margin: 0 }}>
          · {data.total_boxes} korobka · {kg(pallet.weight_g)} kg, {cm(pallet.height_mm)} sm
        </span>

        <span style={{ flex: 1 }} />

        {/* PDF aynan shu yerda: omborga tushadigan qog'oz reja
            o'sha qaror bilan birga chiqadi, keyin alohida bo'limdan
            emas. */}
        <button
          type="button"
          className="btn btn-outlined btn-sm"
          disabled={pdfBusy}
          onClick={() => {
            setPdfError(null);
            setPdfBusy(true);
            openPalletPlanPdf(orderId, maxHeight)
              .catch((error: Error) => setPdfError(error.message))
              .finally(() => setPdfBusy(false));
          }}
        >
          <Icon name="printer" size={14} />
          Reja (PDF)
        </button>
      </div>

      <div className="release-plan-controls">
        <form
          className="pallet-height"
          onSubmit={(event) => {
            event.preventDefault();
            setMaxHeight(heightInput.trim());
          }}
        >
          <label htmlFor={`height-${orderId}`}>Stellaj chegarasi, sm</label>
          <input
            id={`height-${orderId}`}
            className="input"
            inputMode="decimal"
            placeholder={cm(data.limits.max_height_mm)}
            value={heightInput}
            onChange={(event) => setHeightInput(event.target.value)}
          />
          <button type="submit" className="btn btn-outlined btn-sm">
            Qo'llash
          </button>
        </form>

        {data.pallets.length > 1 && (
          <div className="segmented" role="group" aria-label="Pallet">
            {data.pallets.map((item, position) => (
              <button
                key={item.index}
                type="button"
                className={`segmented-item${position === index ? " active" : ""}`}
                aria-pressed={position === index}
                onClick={() => {
                  setIndex(position);
                  setVisibleLayers(99);
                }}
              >
                {item.index}
              </button>
            ))}
          </div>
        )}
      </div>

      {pdfError && (
        <div className="notice" role="alert">
          <Icon name="alert" size={16} />
          <span>{pdfError}</span>
        </div>
      )}

      {/* Reja ishonchsizligi YASHIRILMAYDI: VGH to'liq bo'lmagan
          tovar bilan tuzilgan reja omborda to'g'ri kelmasligi
          mumkin va dispetcher buni chiqarishdan oldin bilishi
          kerak. */}
      {!data.reliable && (
        <div className="notice" role="alert">
          <Icon name="alert" size={16} />
          <span>
            Reja ishonchli emas: {data.missing.length} ta tovarda VGH to'liq emas.
            {data.missing.slice(0, 2).map((item) => (
              <span key={item.key}>
                {" "}
                {item.code} — {item.fields.map((field) => FIELD_NAMES[field] ?? field).join(", ")};
              </span>
            ))}
          </span>
        </div>
      )}
      {data.unplaced.length > 0 && (
        <div className="notice" role="alert">
          <Icon name="alert" size={16} />
          <span>
            {data.unplaced.length} ta tovar palletga joylanmadi:{" "}
            {data.unplaced
              .slice(0, 2)
              .map((item) => `${item.code} — ${UNPLACED_REASONS[item.reason] ?? item.reason}`)
              .join("; ")}
          </span>
        </div>
      )}

      <Suspense fallback={<div className="skeleton" style={{ height: 260 }} />}>
        <PalletScene
          pallet={pallet}
          legend={data.legend}
          limits={data.limits}
          visibleLayers={visibleLayers}
          compact
        />
      </Suspense>

      {/* Tarkib va qatlamlar YOPIQ: qaror oynasida dispetcherga
          kerak bo'lgani «nechta pallet va reja ishonchlimi»;
          qaysi korobka qayerda turgani — teruvchining ishi va u
          PDF bilan omborga tushadi. */}
      <details className="release-plan-details">
        <summary>Tarkibi va qatlamlari</summary>

        <label className="pallet-layers">
          <span>
            Qatlamlar: {layers} / {pallet.layers.length}
          </span>
          <input
            type="range"
            min={1}
            max={pallet.layers.length}
            value={layers}
            onChange={(event) => setVisibleLayers(Number(event.target.value))}
          />
        </label>

        <table className="pallet-legend">
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">Rang</span>
              </th>
              <th scope="col">Artikul / nomi</th>
              <th scope="col">Korobka</th>
            </tr>
          </thead>
          <tbody>
            {legend.map((item) => (
              <tr key={item.key}>
                <td>
                  <span className="pallet-swatch" style={{ background: item.color }} />
                </td>
                <td>
                  <div className="mono">{item.code}</div>
                  <div className="pallet-muted">
                    {item.name}
                    {item.units_per_box ? ` · ${item.units_per_box} dona/kor.` : ""}
                    {flags(item) ? ` · ${flags(item)}` : ""}
                  </div>
                </td>
                <td className="num">{counts.get(item.key) ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      <p className="pallet-muted" style={{ margin: 0 }}>
        {orderNumber} · amaliy joylash tavsiyasi, matematik optimum kafolati emas (OPT-04).
      </p>
    </div>
  );
}
